import { hasPermission, PERMISSIONS, type Permission } from '@repo/contracts';
import { AppShell as SharedAppShell } from '@repo/ui';
import {
  Briefcase,
  Building2,
  CalendarRange,
  FolderKanban,
  Gauge,
  LayoutDashboard,
  ShieldCheck,
} from 'lucide-react';
import { Outlet } from 'react-router';

import { BuildBadge } from '@/components/build-badge';
import { APP_ROUTES } from '@/constants/app-routes';
import { systemRoleLabel } from '@/constants/labels';
import { activeAppRoleOf } from '@/hooks/auth/app-roles';
import { useSignOut } from '@/hooks/auth/use-sign-out';
import { SwitchRoleMenu } from '@/shared/components/switch-role/SwitchRoleMenu';
import {
  selectActiveRole,
  selectCurrentUser,
  selectHeldRoles,
  selectPermissions,
} from '@/store/auth.slice';
import { useAppSelector } from '@/store/store';

const NAV_ITEMS = [
  {
    to: APP_ROUTES.dashboard,
    label: 'Dashboard',
    icon: LayoutDashboard,
    end: true,
    permission: PERMISSIONS.projectRead,
  },
  {
    to: APP_ROUTES.clients.list,
    label: 'Clients',
    icon: Building2,
    permission: PERMISSIONS.clientRead,
  },
  {
    to: APP_ROUTES.projects.list,
    label: 'Projects',
    icon: FolderKanban,
    permission: PERMISSIONS.projectRead,
  },
  {
    to: APP_ROUTES.allocations.list,
    label: 'Allocation',
    icon: CalendarRange,
    permission: PERMISSIONS.allocationRead,
  },
  {
    to: APP_ROUTES.utilisation.list,
    label: 'Utilisation',
    icon: Gauge,
    permission: PERMISSIONS.allocationRead,
  },
  {
    to: APP_ROUTES.approvals,
    label: 'Approvals',
    icon: ShieldCheck,
    permission: PERMISSIONS.projectApprove,
  },
  {
    to: APP_ROUTES.admin.resourceRoles,
    label: 'Resource roles',
    icon: Briefcase,
    permission: PERMISSIONS.resourceRoleRead,
    group: 'Administration',
  },
];

export function AppShell() {
  const user = useAppSelector(selectCurrentUser);
  const effectivePermissions = useAppSelector(selectPermissions);
  const activeRole = useAppSelector(selectActiveRole);
  const heldRoles = useAppSelector(selectHeldRoles);
  const signOut = useSignOut();
  const subtitleRole = activeAppRoleOf(heldRoles, activeRole);

  const canAccess = (permission?: string): boolean =>
    !permission || hasPermission(effectivePermissions, [permission as Permission]);

  return (
    <SharedAppShell
      productName="Cyrino"
      sectionName="Project Management System"
      navItems={NAV_ITEMS}
      canAccess={canAccess}
      fullName={user?.fullName}
      subtitle={subtitleRole ? systemRoleLabel(subtitleRole) : undefined}
      onSignOut={signOut}
      footer={<BuildBadge />}
      accountMenuExtra={<SwitchRoleMenu />}
    >
      <Outlet />
    </SharedAppShell>
  );
}
